import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { cn } from '../../lib/utils'
import type { ImportFunnel } from './importTypes'

interface ImportFunnelRibbonProps {
    org: string
    importUid: string
    funnel: ImportFunnel
}

/**
 * The funnel ribbon (spec §7.2): four metric cards — deposited / unique /
 * classified / exploited — over one thin stacked progress bar showing how much
 * of the deposit survived each stage.
 *
 * Every card links to the documents list filtered by this import, so the
 * numbers are navigable rather than decorative.
 */
export function ImportFunnelRibbon({ org, importUid, funnel }: ImportFunnelRibbonProps) {
    const { t } = useTranslation('imports')

    const stages = [
        { key: 'deposited', value: funnel.deposited, tone: 'bg-muted-foreground/40' },
        { key: 'unique', value: funnel.unique, tone: 'bg-info' },
        { key: 'classified', value: funnel.classified, tone: 'bg-warning' },
        { key: 'exploited', value: funnel.exploited, tone: 'bg-success' },
    ] as const

    // The bar is drawn against `deposited` — the widest stage — so each segment
    // reads as "share of the deposit that made it this far".
    const total = Math.max(funnel.deposited, 1)

    return (
        <section data-testid="import-funnel" className="space-y-3">
            <h2 className="text-sm font-medium text-muted-foreground">{t('funnel.title')}</h2>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {stages.map(stage => (
                    <Link
                        key={stage.key}
                        to="/$org/documents"
                        params={{ org }}
                        search={{
                            asset_uid: undefined,
                            document_type_uid: undefined,
                            flags: undefined,
                            import_uid: importUid,
                            limit: 100,
                        }}
                        data-testid={`import-funnel-${stage.key}`}
                        className="rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary/40"
                    >
                        <div className="flex items-center gap-2">
                            <span className={cn('h-2 w-2 rounded-full', stage.tone)} />
                            <span className="text-sm font-medium text-foreground">
                                {t(`funnel.${stage.key}`)}
                            </span>
                        </div>
                        <p
                            className="mt-2 text-3xl font-bold text-foreground"
                            data-testid={`import-funnel-${stage.key}-value`}
                        >
                            {stage.value}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                            {t(`funnel.${stage.key}_hint`)}
                        </p>
                    </Link>
                ))}
            </div>

            {/*
              Each stage is a SUBSET of the previous one, so the segments are
              stacked in depth (widest at the back), not side by side — laying
              them out in a row would sum well past 100% and misread as
              "deposited + unique + classified + exploited".
            */}
            <div
                className="relative h-2 w-full overflow-hidden rounded-full bg-muted"
                data-testid="import-funnel-bar"
                role="img"
                aria-label={t('funnel.title')}
            >
                {stages.map(stage => (
                    <div
                        key={stage.key}
                        className={cn('absolute inset-y-0 left-0 rounded-full', stage.tone)}
                        style={{ width: `${Math.min(100, (stage.value / total) * 100)}%` }}
                    />
                ))}
            </div>

            <p className="text-xs text-muted-foreground" data-testid="import-extracted-values">
                {t('funnel.extracted_values', { count: funnel.extracted_values })}
            </p>

            {/*
              Archive entries that never became a document (spec 2026-08-16-07
              §1). They sit *before* "deposited", so they get a line of their
              own rather than a card: there is nothing to navigate to.
            */}
            {(funnel.skipped_duplicates > 0 || funnel.skipped_junk > 0) && (
                <p className="text-xs text-muted-foreground" data-testid="import-archive-skips">
                    {t('funnel.archive_skips', {
                        duplicates: funnel.skipped_duplicates,
                        junk: funnel.skipped_junk,
                    })}
                </p>
            )}
            {funnel.failed_entries > 0 && (
                <p className="text-xs text-destructive" data-testid="import-archive-failed-entries">
                    {t('funnel.failed_entries', { count: funnel.failed_entries })}
                </p>
            )}
        </section>
    )
}
